import { useEffect, useState } from 'react'
import { useLanguage } from '@/localization'

/**
 * Shared community data — the canonical Uliunai identity (Discord, socials,
 * leadership, code of conduct). Sourced once from `GET /api/community`
 * (Vite proxy → platform API on :8078). Modeled on the news fetch pattern in
 * `NewsFeed.tsx`. A hardcoded seed is inlined as a fallback so the hub still
 * renders its community identity if the API is briefly unreachable.
 */

/** A social/external community link. */
export interface SocialLink {
  label: string
  href: string
  /** Icon key (e.g. "discord", "steam"). */
  icon: string
}

/** How prominently a team member is shown (controls card size). */
export type TeamTier = 'core' | 'admin' | 'moderator'

/** Which game sites a team member appears on. */
export type TeamScope = 'platform' | 'kf1' | 'minecraft'

/** A platform/game team member entry. */
export interface LeadershipMember {
  name: string
  role: string
  /** How prominently they're shown: core (big), admin (medium), moderator (compact). */
  tier: TeamTier
  /** "platform" for network-wide; a game id for game-specific staff. */
  scope: TeamScope
  /** Short role pill shown on the card (e.g. "Owner", "Core Team", "Admin", "Mod"). */
  badge?: string
  /** Avatar image URL; falls back to `initials` on error / when absent. */
  avatar?: string
  /** 2-letter fallback rendered when no avatar is available. */
  initials?: string
  /** Longer blurb shown on richer (core/admin) cards. */
  description?: string
  /** Skill chips shown on core cards. */
  specialties?: string[]
}

/** A universal code-of-conduct category: a short summary plus its detailed rules. */
export interface ConductRule {
  title: string
  summary: string
  /** The detailed rules under this category (shown on the full /conduct page). */
  rules?: string[]
}

/** The full community payload returned by `GET /api/community`. */
export interface CommunityData {
  social: SocialLink[]
  discordInvite: string
  leadership: LeadershipMember[]
  conduct: ConductRule[]
}

/**
 * Hardcoded fallback — keeps static identity (especially the Discord invite)
 * rendering even when the API is down. Mirrors the committed seed in
 * `platform/api/data/community.json`.
 */
export const COMMUNITY_FALLBACK: CommunityData = {
  social: [
    { label: 'Discord', href: 'https://discord.gg/vGX8GSrej6', icon: 'discord' },
    { label: 'Steam', href: 'https://steamcommunity.com/groups/uliunailt', icon: 'steam' },
  ],
  discordInvite: 'https://discord.gg/vGX8GSrej6',
  leadership: [
    {
      name: 'Lytair',
      role: 'Owner & Community Lead',
      tier: 'core',
      scope: 'platform',
      badge: 'Owner',
      avatar: '/avatars/uliunai_avatar_lytair.png',
      initials: 'LY',
      description:
        'Leads the Uliunai.com community. Responsible for community moderation and ensuring a welcoming environment for all players.',
      specialties: ['Community Moderation', 'Player Support'],
    },
    {
      name: 'Greiton',
      role: 'Platform Engineer',
      tier: 'core',
      scope: 'platform',
      badge: 'Core Team',
      avatar: '/avatars/uliunai_avatar_greiton.png',
      initials: 'GR',
      description:
        'Built the Uliunai.com platform from the ground up. Handles the website, admin tools, server infrastructure, and deployment pipeline.',
      specialties: ['Web Development', 'Platform Design', 'Server Infrastructure'],
    },
    {
      name: 'HekuT',
      role: 'Game Developer',
      tier: 'core',
      scope: 'platform',
      badge: 'Core Team',
      avatar: '/avatars/uliunai_avatar_hekut.png',
      initials: 'HT',
      description:
        'The mind behind the gameplay. Develops custom mutators, perks, and weapons, and fine-tunes game balance to keep every match fresh and challenging.',
      specialties: ['Game Balancing', 'Mutator Development', 'Custom Content'],
    },
    {
      name: 'Jackie',
      role: 'Discord Administrator',
      tier: 'admin',
      scope: 'platform',
      badge: 'Admin',
      avatar: '/avatars/uliunai_avatar_jackie.png',
      initials: 'JA',
      description: 'Keeps the Discord running and supports the community day to day.',
    },
    {
      name: 'mez',
      role: 'Administrator',
      tier: 'admin',
      scope: 'platform',
      badge: 'Admin',
      avatar: '/avatars/uliunai_avatar_mez.png',
      initials: 'ME',
      description: 'Part of the admin team.',
    },
    { name: 'habib', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_habib.png', initials: 'HB' },
    { name: 'Hakk', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_hakk.png', initials: 'HK' },
    { name: 'Paisely', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_paisely.png', initials: 'PA' },
    { name: 'Spagh', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_spagh.png', initials: 'SP' },
  ],
  conduct: [
    {
      title: 'Be Respectful',
      summary: 'No insulting, provoking, harassment or discrimination.',
      rules: [
        "Treat everyone the way you'd want to be treated — veterans and newcomers alike.",
        'No harassment, hate speech, racism, sexism, or discrimination of any kind.',
        'No personal attacks, insulting, or deliberately provoking and starting drama.',
        'No witch-hunting or rallying the community against another member.',
        "Respect privacy — never share anyone's personal information (doxxing).",
        'Keep it family-friendly: no NSFW, gore, shock, or illegal content.',
      ],
    },
    {
      title: 'Fair Play',
      summary: 'No cheating, exploits, or third-party software.',
      rules: [
        'No cheats, hacks, macros, or third-party software that give an unfair advantage.',
        'No exploiting bugs, glitches, or unintended mechanics — report them to staff instead.',
        'No boosting, win-trading, or manipulating stats and leaderboards.',
        'Play legit and earn your progress.',
      ],
    },
    {
      title: 'Keep Chat Clean',
      summary: 'No spam, flooding, impersonation, or advertising.',
      rules: [
        'No spamming, flooding, excessive caps, or chain messages in text or voice.',
        'No advertising other servers, communities, products, or services without permission.',
        'No mic spam, soundboards, or voice changers used to annoy others.',
        'Keep channels on-topic and use the right channel for the right thing.',
        'Use English in shared/global channels so staff can moderate fairly.',
      ],
    },
    {
      title: 'Accounts & Security',
      summary: 'One account per player, kept secure and yours alone.',
      rules: [
        'One account per person — alternate accounts need staff approval.',
        "You are responsible for everything done on your account; don't share it.",
        'No impersonating staff, other members, or anyone else.',
        'No real-money trading — buying or selling accounts, ranks, or in-game items for cash.',
      ],
    },
    {
      title: 'Staff & Enforcement',
      summary: 'Staff keep things fair — follow their calls and appeal calmly.',
      rules: [
        "Follow staff instructions; they're here to keep the community fair and fun.",
        "Don't argue with or backseat-moderate staff in public — raise concerns politely.",
        'Punishments scale with severity: warning → mute → kick → temporary ban → permanent ban.',
        'Ban evasion (using alts to dodge a punishment) resets and extends it.',
        'Staff have the final say on enforcement; honest mistakes are handled fairly.',
      ],
    },
    {
      title: 'Reporting & Appeals',
      summary: "See a problem? Report it — don't take matters into your own hands.",
      rules: [
        'Report rule-breakers through a Discord support ticket or by DMing an admin.',
        "Don't retaliate or take justice into your own hands — let staff handle it.",
        'Appeal bans or mutes through Discord tickets, not public chat.',
        'Include evidence (screenshots or clips) where you can — it helps staff act quickly.',
      ],
    },
  ],
}

/** Narrow an unknown API payload into a complete CommunityData, filling gaps from the seed. */
function normalize(data: unknown): CommunityData {
  const d = (data ?? {}) as Partial<CommunityData>
  return {
    social: Array.isArray(d.social) && d.social.length > 0 ? d.social : COMMUNITY_FALLBACK.social,
    discordInvite:
      typeof d.discordInvite === 'string' && d.discordInvite
        ? d.discordInvite
        : COMMUNITY_FALLBACK.discordInvite,
    leadership:
      Array.isArray(d.leadership) && d.leadership.length > 0
        ? d.leadership
        : COMMUNITY_FALLBACK.leadership,
    conduct:
      Array.isArray(d.conduct) && d.conduct.length > 0 ? d.conduct : COMMUNITY_FALLBACK.conduct,
  }
}

/**
 * Fetch the shared community data once on mount. Always returns usable data:
 * the inlined fallback renders immediately, then the API response (if any)
 * replaces it. Never throws — a failed fetch simply keeps the fallback.
 */
export function useCommunity(): CommunityData {
  const { apiPath } = useLanguage()
  const [data, setData] = useState<CommunityData>(COMMUNITY_FALLBACK)

  useEffect(() => {
    const controller = new AbortController()

    async function load() {
      try {
        const res = await fetch(apiPath('/api/community'), { signal: controller.signal })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json: unknown = await res.json()
        setData(normalize(json))
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        // Keep the fallback already in state.
      }
    }

    load()
    return () => controller.abort()
  }, [apiPath])

  return data
}
